/* eslint-disable indent */
import type { FC } from 'react';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { assignRef, useCallbackRef } from 'use-callback-ref';

import { PageBackButton } from '@src/common-components/button';
import { ArrowLeftIcon2, Skeleton } from '@src/components';
import {
  FlatSteps,
  HouseSteps,
  PAYMENT_SUCCESS_ROUTE,
  PaymentStatus,
  Product,
  analyticEvents,
  nonSubscriptionProducts,
} from '@src/constants';
import { GlobalErrorInfo } from '@src/features';
import {
  useClearWizardStore,
  useCustomerEventRegister,
  useIsDesktop,
  useRequest,
} from '@src/hooks';
import { useClearStoreProducts } from '@src/hooks/use-clear-store-products';
import { Store, UserActionTypes } from '@src/store';
import {
  cleartDraft,
  handleAnalyticsEvents,
  scrollToIframe,
  sendFlatProgress,
  sendHouseProgress,
  useScrollToIframeOnMount,
} from '@src/utils';

import { BaseLayout } from '../../layouts/base-layout';
import { StyledIframe } from './best-to-pay-page.styles';

type ConfirmPaymentType = {
  subscriptionId: string;
  paymentDocumentId: string;
  operationId: string;
  id: string;
  contractId: string;
  amount: string;
  consents: {
    consentType: string;
  }[];
  paymentGateOrder: string;
};

export const BestToPayStartPage: FC = () => {
  const {
    state: {
      stateOrder: {
        orderInitData,
        orderSubmitData,
        completePaymentData,
        hasSpecialOffersConsent,
      },
      stateAuth: { authTokens },
      stateUser: { selectedProduct, isPrivateOffice },
    },
    dispatch,
  } = useContext(Store);
  const { t } = useTranslation();
  const clearWizarsStore = useClearWizardStore();
  const clearStoreProducts = useClearStoreProducts();
  const isDesktop = useIsDesktop();
  const [isRetry, setRetry] = useState('');
  const [isShowBackButton, setIsShowBackButton] = useState(true);
  const refCount = useRef<number>(0);

  const preset = localStorage.getItem('preset');
  const currentSelectedProduct = localStorage.getItem('selectedProduct');

  const onRefRetrydUpdate = (newValue: any) => {
    if (newValue && newValue !== null && refCount.current === 0) {
      refCount.current = 1;
      setRetry(String(newValue));
    }
  };

  const onRefSOperationIdUpdate = (newValue: any) => {
    if (newValue && newValue !== null) {
      completPayment.operationId = newValue;

      if (!!completPayment.id) {
        refetch().then(() => {
          scrollToIframe();
        });
      }
    }
  };

  const refRetry = useCallbackRef<null | string>(null, onRefRetrydUpdate);

  const refSOperationId = useCallbackRef<null | string>(
    null,
    onRefSOperationIdUpdate
  );

  const navigate = useNavigate();

  useEffect(() => {
    if (!orderInitData) {
      navigate('/');
    }
  }, [orderInitData]);

  const storageListener = () => {
    if (
      localStorage.getItem('operationId') !== null &&
      refSOperationId.current !== localStorage.getItem('operationId')
    ) {
      cleartDraft();
      assignRef(refSOperationId, String(localStorage.getItem('operationId')));
      setIsShowBackButton(false);
    }

    if (
      localStorage.getItem('retryPayment') !== null &&
      refRetry.current !== String(localStorage.getItem('retryPayment'))
    ) {
      assignRef(refRetry, String(localStorage.getItem('retryPayment')));

      handleAnalyticsEvents.handleRetryPaymentAnalyticsEvents({
        selectedProduct,
        preset,
      });
    }

    if (localStorage.getItem('paymentDecline') !== null) {
      setIsShowBackButton(false);
    }

    if (localStorage.getItem('paymentResult') === 'success') {
      cleartDraft();
      clearStoreProducts();
    }

    if (localStorage.getItem('paymentResult') !== null) {
      navigate('/', { state: { prevPath: PAYMENT_SUCCESS_ROUTE } });
      clearWizarsStore();

      if (selectedProduct !== Product.PETS_TELEVET_PLUS) {
        localStorage.removeItem('selectedProduct');

        dispatch({
          type: UserActionTypes.SetSelectedProduct,
          payload: undefined,
        });

        dispatch({
          type: UserActionTypes.SetCachedProduct,
          payload: undefined,
        });
      }
    }
  };

  const sector = `sector=${orderInitData?.sector}`;
  const id = `id=${orderInitData?.id}`;
  const signature = `signature=${orderInitData?.signature}`;
  const url = `url=${orderInitData?.url}`;
  const failUrl = `failUrl=${orderInitData?.failUrl}`;
  const get_token = 1;

  const completPayment: ConfirmPaymentType = {
    subscriptionId:
      completePaymentData?.subscriptionId ||
      orderSubmitData?.subscriptionId ||
      '',
    paymentDocumentId:
      completePaymentData?.billId || orderSubmitData?.billId || '',
    operationId: '',
    id: orderInitData?.id || '',
    contractId:
      completePaymentData?.contractId || orderSubmitData?.contractId || '',
    amount:
      String(
        completePaymentData?.premiumAndDelta || orderSubmitData?.premiumAndDelta
      ) || '',
    paymentGateOrder: orderInitData?.paymentGateOrder || '',
    consents: [
      ...(hasSpecialOffersConsent
        ? [
            {
              consentType: '002',
            },
          ]
        : []),
      ...(nonSubscriptionProducts.includes(selectedProduct || '')
        ? []
        : [
            {
              consentType: '001',
            },
          ]),
    ],
  };

  const { isLoading, error, res, refetch } = useRequest(
    'orderCompletePayment',
    'post',
    '/v1/payment/complete-payment',
    completPayment,
    [completPayment],
    true,
    authTokens?.authorization?.accessToken
  );

  useEffect(() => {
    switch (selectedProduct) {
      case Product.APARTMENT:
        if (error) {
          sendFlatProgress(FlatSteps.PAYMENT_RESULT, PaymentStatus.DECLINE);
        } else if (res) {
          sendFlatProgress(FlatSteps.PAYMENT_RESULT, PaymentStatus.SUCCESS);
        }
        break;
      case Product.HOUSE:
        if (error) {
          sendHouseProgress(HouseSteps.PAYMENT_RESULT, PaymentStatus.DECLINE);
        } else if (res) {
          sendHouseProgress(HouseSteps.PAYMENT_RESULT, PaymentStatus.SUCCESS);
        }
        break;
    }
  }, [error, res]);

  const goBack = () => {
    navigate(-1);
  };

  const { refetch: refetchCustomerEventRegister } = useCustomerEventRegister({
    eventCode: analyticEvents.paymentSuccess,
  });

  useEffect(() => {
    if (!isLoading && res) {
      refetchCustomerEventRegister();
      assignRef(refSOperationId, undefined);
      localStorage.removeItem('referenceSuccess');
      localStorage.removeItem('operationId');
      localStorage.removeItem('paymentDecline');
      localStorage.removeItem('paymentResult');
    }
  }, [isLoading, res]);

  useEffect(() => {
    if (isRetry !== '') {
      setRetry('');
      setIsShowBackButton(true);
      refCount.current = 0;
      localStorage.removeItem('retryPayment');
    }
  }, [isRetry]);

  useScrollToIframeOnMount();

  useEffect(() => {
    window.addEventListener('storage', storageListener);

    switch (currentSelectedProduct) {
      case Product.APARTMENT:
        sendFlatProgress(FlatSteps.PURCHASE_FLAT_TO_STEP_9_PAY);
        break;
      case Product.HOUSE:
        sendHouseProgress(HouseSteps.PURCHASE_FLAT_TO_STEP_9_PAY);
        break;
    }

    return () => {
      window.removeEventListener('storage', storageListener);

      localStorage.removeItem('paymentResult');
    };
  }, []);

  // TODO: доработать на этапе создания страницы выбора карты оплаты
  // const token = undefined;
  // const action = undefined;

  const propsString = [sector, id, signature, url, failUrl, get_token].join(
    '&'
  );

  const route = `Purchase?${propsString}`;

  if (error) return <GlobalErrorInfo retryHandler={refetch} />;

  return (
    <BaseLayout>
      {isShowBackButton && (
        <PageBackButton
          variant="text"
          icon={<ArrowLeftIcon2 />}
          onClick={goBack}
        >
          {t('COMMON:buttons.back')}
        </PageBackButton>
      )}
      {!isDesktop && isLoading && <Skeleton />}
      {isRetry === '' ? (
        <StyledIframe
          isPrivateOffice={isPrivateOffice}
          src={`${window.envUrls.BEST2PAY_API_URL}/${route}`}
          allow="payment"
          sandbox="allow-top-navigation allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
        />
      ) : null}
    </BaseLayout>
  );
};
